import { t } from "../i18n";
import { castName } from "../art/cast";
import { $, h } from "../lib/dom";
import { clamp } from "../lib/util";
import { P } from "./player";
import { S } from "./state";
import { COLS, ROWS, T } from "./world";

/**
 * Narrow portrait screens can't show the 20×12 map at a readable size, so the
 * map is drawn at 2× (32px tiles) and the view follows the player. Characters
 * flagged "!"/"?" outside the view get an edge marker. "看全圖" switches back
 * to fitting the whole map.
 */
const CAM_SCALE = 2;
const portrait = matchMedia("(max-width: 900px) and (orientation: portrait)");

const cam = {
  /** Player preference: follow-camera on (default) or whole map. */
  zoomed: true,
  /** Follow-camera is actually in use for the current layout. */
  active: false,
  /** Last applied x offset in screen px, to avoid restyling every frame. */
  offset: Number.NaN,
};

const cv = $<HTMLCanvasElement>("#cv");
const stage = $("#stage");
const camBtn = $<HTMLButtonElement>("#camBtn");
const marks = $("#edgeMarks");

/** Size the stage and map canvas for the current layout. */
export function fitCanvas(): void {
  const W = stage.clientWidth;
  const fitW = W / (COLS * T);
  let s: number;
  if (portrait.matches) {
    // Portrait: the stage height follows the map (the log drawer takes the rest).
    cam.active = cam.zoomed && fitW < CAM_SCALE;
    s = cam.active ? CAM_SCALE : fitW;
    stage.style.height = `${Math.round(ROWS * T * s)}px`;
  } else {
    cam.active = false;
    stage.style.height = "";
    s = Math.min(fitW, stage.clientHeight / (ROWS * T));
    if (s >= 2) s = Math.floor(s * 4) / 4;
  }
  cv.style.width = `${Math.floor(COLS * T * s)}px`;
  cv.style.height = `${Math.floor(ROWS * T * s)}px`;
  stage.classList.toggle("cam", cam.active);
  camBtn.hidden = !(portrait.matches && fitW < CAM_SCALE);
  camBtn.textContent = cam.zoomed ? t("stage.fullMap") : t("stage.zoomIn");
  cam.offset = Number.NaN;
  if (!cam.active) {
    cv.style.transform = "";
    clearEdgeMarks();
  }
}

/** Per frame: keep the player centred (clamped to the map) and update edge markers. */
export function updateCamera(): void {
  if (!cam.active) return;
  const W = stage.clientWidth;
  const viewW = W / CAM_SCALE;
  const camX = clamp((P.fx + 0.5) * T - viewW / 2, 0, COLS * T - viewW);
  const offset = Math.round(camX * CAM_SCALE);
  if (offset !== cam.offset) {
    cam.offset = offset;
    cv.style.transform = `translateX(${-offset}px)`;
  }
  updateEdgeMarks(camX, viewW);
}

const markEls = new Map<string, HTMLElement>();

function clearEdgeMarks(): void {
  markEls.clear();
  marks.replaceChildren();
}

function updateEdgeMarks(camX: number, viewW: number): void {
  const seen = new Set<string>();
  for (const e of S.ents) {
    if (e.hidden) continue;
    const f = S.speaking === e.id || S.speaking === e.k ? "…" : S.flags[e.id];
    if (!f) continue;
    const cx = (e.fx + 0.5) * T;
    const side = cx < camX ? "l" : cx > camX + viewW ? "r" : null;
    if (!side) continue;
    seen.add(e.id);
    let el = markEls.get(e.id);
    if (!el) {
      el = h("div", { className: "edge" });
      markEls.set(e.id, el);
      marks.appendChild(el);
    }
    // Full-width marks: the pixel font draws "!" like "|".
    const g = f === "!" ? "！" : f === "?" ? "？" : f; // i18n-ok: glyph choice, not text
    const label = side === "l" ? `◀ ${g} ${castName(e.k)}` : `${castName(e.k)} ${g} ▶`;
    if (el.textContent !== label) el.textContent = label;
    el.className = `edge ${side}` + (f === "!" ? " news" : f === "?" ? " ask" : "");
    el.style.top = `${clamp((e.fy * T - 4) * CAM_SCALE, 30, stage.clientHeight - 30)}px`;
  }
  for (const [id, el] of markEls)
    if (!seen.has(id)) {
      el.remove();
      markEls.delete(id);
    }
}

export function initCamera(): void {
  camBtn.onclick = () => {
    cam.zoomed = !cam.zoomed;
    fitCanvas();
  };
  portrait.addEventListener("change", fitCanvas);
}
